import React from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash.clonedeep';
import { Row, Col, Button, Badge, ListGroup, ListGroupItem,
  ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem,
  Media, Tooltip, Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import StackedBarChart2 from '../Charts/StackedBarChart2.jsx';

dayjs.extend(LocalizedFormat)

class ListPanel extends React.Component {
  constructor(props) {
    super();
    this.state = {
      currentPage: 0,
      currentPageSeg: 0,
    };
    this.currentSubsetList = [];
    this.timelineDataInfo = [];
  }
  listCount = 7;
  pageSegRadius = 5;
  timeSegmentCount = 40;

  componentWillReceiveProps(nextProps) {
    if(nextProps.filterTimeStamp !== this.props.filterTimeStamp ||
      nextProps.category !== this.props.category) {
      this.state.currentPage = 0;
      this.state.currentPageSeg = 0;
      this.updateListInfo(this.state, nextProps);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if(nextState !== this.state)
      return true;
    if(nextProps.filterTimeStamp !== this.props.filterTimeStamp ||
      nextProps.category !== this.props.category)
      return true;
    if(this.showingList !== this.currentSubsetList)
      return true;
    return false
  }

  getPageCount(props, listCount) {
    let pageCount = 0;
    if(props.category === 'organism') {
      pageCount = props.filtered.filterOrganisms ? Math.floor((props.filtered.filterOrganisms.length-1)/listCount) + 1 : 0;
    }
    else if(props.category === 'team') {
      pageCount = props.filtered.filterTeams ? Math.floor((props.filtered.filterTeams.length-1)/listCount) + 1 : 0;
    }
    return pageCount;
  }

  correctCurrentPage(currentPage, pageCount) {
    if(currentPage < 0) currentPage = 0;
    if(currentPage >= pageCount) currentPage = pageCount-1;
    return currentPage;
  }

  getCurrentPageSegRange(currentPage, pageSegRadius, pageCount) {
    let currentSegPageMin = currentPage - pageSegRadius;
    let currentSegPageMax = currentPage + pageSegRadius;
    let pageSegLength = pageSegRadius * 2 + 1;
    if(currentSegPageMax >= pageCount) {
      currentSegPageMax = pageCount-1;
      currentSegPageMin = pageCount-pageSegLength;
    }
    if(currentSegPageMin < 0) {
      currentSegPageMin = 0;
      currentSegPageMax = pageSegLength-1;
    }
    return [currentSegPageMin, currentSegPageMax];
  }

  correctCurrentPageSeg(currentPageSeg, pageSegRadius, segRange, pageCount) {
    let pageSegLength = pageSegRadius * 2 + 1;
    let minPageSegNum = -Math.ceil(segRange[0] / pageSegLength);
    let maxPageSegNum = Math.floor((pageCount - segRange[0] - 1) / pageSegLength);
    if(currentPageSeg < minPageSegNum)
      currentPageSeg = minPageSegNum;
    if(currentPageSeg > maxPageSegNum)
      currentPageSeg = maxPageSegNum;
    return currentPageSeg;
  }

  selectSubset = (currentItem => {
    if(this.props.category === 'organism')
      this.props.onOrganismSelected(currentItem);
    else if(this.props.category === 'team')
      this.props.onTeamSelected(currentItem);
  }).bind(this);

  changePageNum = (currentPage => {
    let pageCount = this.getPageCount(this.props, this.listCount);
    currentPage = this.correctCurrentPage(currentPage);
    this.setState({currentPage, currentPageSeg: 0});
    this.updateListInfo({currentPage}, this.props);
  }).bind(this);

  changePageSegNum = (currentPageSeg => {
    let {currentPage} = this.state;
    let pageCount = this.getPageCount(this.props, this.listCount);
    currentPage = this.correctCurrentPage(currentPage);
    let segRange = this.getCurrentPageSegRange(currentPage, this.pageSegRadius, pageCount);
    currentPageSeg = this.correctCurrentPageSeg(currentPageSeg, this.pageSegRadius, segRange, pageCount);
    this.setState({currentPageSeg});
  }).bind(this);

  updateListInfo(state, props) {
    if(!props.base || !props.filtered)
      return;
    let {
      listEMMethod,
      dataListEMDs,
    } = props.base;
    let {
      filterResults,
      filterMap,
      filterOrganisms,
      filterTeams,
    } = props.filtered;
    if(!dataListEMDs) dataListEMDs = [];
    if(!filterResults) filterResults = [];
    if(!filterOrganisms) filterOrganisms = [];
    if(!filterTeams) filterTeams = [];

    // three months for a time segment
    this.timeSegments = [];
    let lastTimeTick = dayjs().subtract((dayjs().month() % 3), 'month').date(1);
    for(let i=0; i<this.timeSegmentCount; ++i) {
      let start = lastTimeTick.subtract((this.timeSegmentCount-i)*3, 'month');
      let end = start.add(3, 'month');
      this.timeSegments.push({start, end, id: +i, maps: [],
        name: `${start.format('ll')} - ${end.format('ll')}`});
    }
    // segment filtered result by date
    filterResults.forEach(i => {
      let segIdx = this.timeSegmentCount - Math.floor((lastTimeTick.diff(dayjs(dataListEMDs[i].t), 'month') + 3) / 3);
      if(segIdx < 0 || segIdx >= this.timeSegments.length)
        return;
      this.timeSegments[segIdx].maps.push(i);
    })
    // build timeline data for each organism/team
    if(props.category === 'organism') {
      let {currentPage} = state;
      if(this.listCount*currentPage >= filterOrganisms.length)
        currentPage = Math.floor((filterOrganisms.length-1)/this.listCount);
      this.currentSubsetList = filterOrganisms.slice(this.listCount*currentPage,
        this.listCount*(currentPage+1)).map((o, index) => ({
        index: this.listCount*currentPage + index,
        name: o.name === '(Unknown)' ? 'Unknown' : `${o.name} (${o.ncbi})`,
        timelineData: this.timeSegments.map(seg => {
          let counterMethod = listEMMethod.map(m => 0);
          seg.maps.forEach(i => {
            if(dataListEMDs[i].o.indexOf(o.id) >= 0)
              counterMethod[dataListEMDs[i].m]++;
          });
          return {
            name: seg.name,
            id: +seg.id,
            values: counterMethod,
          };
        })
      }))
    }
    else if(props.category === 'team') {
      let {currentPage} = state;
      if(this.listCount*currentPage >= filterTeams.length)
        currentPage = Math.floor((filterTeams.length-1)/this.listCount);
      this.currentSubsetList = filterTeams.slice(this.listCount*currentPage,
        this.listCount*(currentPage+1)).map((t, index) => ({
        index: this.listCount*currentPage + index,
        name: t.name,
        timelineData: this.timeSegments.map(seg => {
          let counterMethod = listEMMethod.map(m => 0);
          seg.maps.forEach(i => {
            if(dataListEMDs[i].u.indexOf(t.id) >= 0)
              counterMethod[dataListEMDs[i].m]++;
          });
          return {
            name: seg.name,
            id: +seg.id,
            values: counterMethod,
          };
        })
      }))
    }
  }

  render() {
    let {sizeFactor} = this.props;
    if(!sizeFactor) sizeFactor = 1;

    let {currentPage, currentPageSeg} = this.state;
    let pageCount = this.getPageCount(this.props, this.listCount);
    currentPage = this.correctCurrentPage(currentPage);
    let segRange = this.getCurrentPageSegRange(currentPage, this.pageSegRadius, pageCount);
    currentPageSeg = this.correctCurrentPageSeg(currentPageSeg, this.pageSegRadius, segRange, pageCount);
    let pageSegLength = this.pageSegRadius*2+1;
    segRange[0] += pageSegLength * currentPageSeg;
    segRange[1] += pageSegLength * currentPageSeg;

    let pageList = [];
    if(pageCount) {
      for(let i=segRange[0]; i<pageCount && i<=segRange[1]; ++i)
        pageList.push(i);
    }

    if(!this.showingList || this.showingList.length != this.currentSubsetList.length)
      setTimeout(this.forceUpdate.bind(this)); // hack for ListGroupItem render delay problem
    this.showingList = this.currentSubsetList;
    return (
      <div>
        <ListGroup>
          {this.currentSubsetList.map((t, index) => (
            <ListGroupItem tag="button" action
              onClick={() => this.selectSubset(t.index)}
            >
            <Row>
              <Col sm={4} className="list-title">
                {'#' + t.index + ' ' + t.name}
              </Col>
              <Col sm={8}>
                <StackedBarChart2
                  width={350 * sizeFactor}
                  height={40 * sizeFactor}
                  thumbMode={true}
                  listRange={[0, this.timeSegmentCount]}
                  schema={this.props.filtered.compareDataInfo}
                  data={t.timelineData}
                />
              </Col>
            </Row>
            </ListGroupItem>
          ))}
        </ListGroup>
        {pageCount > 0 && <Pagination size="sm" aria-label="Page navigation">
          <PaginationItem>
            <PaginationLink onClick={() => this.changePageNum(0)}>{'«'}</PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink onClick={() => this.changePageNum(currentPage-1)}>{'‹'}</PaginationLink>
          </PaginationItem>
          {pageList[0] === 0 ? [] : <PaginationItem>
            <PaginationLink onClick={() => this.changePageSegNum(currentPageSeg-1)}>{'⋯'}</PaginationLink>
          </PaginationItem>}
          {pageList.map(i => (
            i === currentPage ? <PaginationItem active>
              <PaginationLink>
                {i+1}
              </PaginationLink>
            </PaginationItem> : <PaginationItem>
              <PaginationLink onClick={() => this.changePageNum(i)}>
                {i+1}
              </PaginationLink>
            </PaginationItem>
          ))}
          {pageList[pageList.length-1] === pageCount-1 ? [] : <PaginationItem>
            <PaginationLink onClick={() => this.changePageSegNum(currentPageSeg+1)}>{'⋯'}</PaginationLink>
          </PaginationItem>}
          <PaginationItem>
            <PaginationLink onClick={() => this.changePageNum(currentPage+1)}>{'›'}</PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink onClick={() => this.changePageNum(pageCount-1)}>{'»'}</PaginationLink>
          </PaginationItem>
        </Pagination>}
      </div>
    )
  }
}

ListPanel.propTypes = {
  listEMMethod: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
  })),
  dataListEMDs: PropTypes.arrayOf(PropTypes.shape({
    m: PropTypes.number.isRequired,
    c: PropTypes.arrayOf(PropTypes.number).isRequired,
    o: PropTypes.arrayOf(PropTypes.number).isRequired,
    a: PropTypes.arrayOf(PropTypes.number).isRequired,
    u: PropTypes.arrayOf(PropTypes.number).isRequired,
    s: PropTypes.number.isRequired,
    p: PropTypes.number.isRequired,
    r: PropTypes.number.isRequired,
    t: PropTypes.instanceOf(Date).isRequired,
    g: PropTypes.number.isRequired,
  })),
}

export default ListPanel;
